import { Filder } from '@/project/documents/types'
import store from '@/store/'
import { GlobalState } from '@/store/modules/global/state'
import { WORD_EXTENSIONS } from '../utils/office'

type PendoEvent =
  | 'create_checklist'
  | 'checklist_import_started'
  | 'checklist_import_completed'
  | 'checklist_import_uploaded'
  | 'create_home_folder'
  | 'duplicate_M365_document'
  | 'open_M365_document'
  | 'request_document_ai_summary'
  | 'swap_success'
  | 'redact_started'
  | 'redaction_mode_used'
  | 'redact_by_keywords'
  | 'redact_by_zone'
  | 'redact_cancelled'
  | 'redact_saved'
  | 'redaction_started'
  | 'document_uploaded'
  | 'document_previewed'
  | 'document_downloaded'
  | 'project_closed'
  | 'project_bible'
  | 'user_added'
  | 'mass_user_added'
  | 'create_dr'
  | 'create_tm'
  | 'question_created'
  | 'mass_question_created'
  | 'question_answered'
  | 'question_closed'
  | 'question_deleted'
  | 'new_version_created'

declare global {
  interface Window {
    // We disable it because we don't manage that type, and they don't provide a type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pendo: {
      track (eventName: PendoEvent, payload?: Record<PropertyKey, unknown>): void
    }
  }
}

/**
 * A function that wraps the pendo agent track method safely.
 * Can also be used to add more information to every tracked event.
 * @param {string} eventName Name of the event to track
 * @param {object} payload Object containing properties to send to Pendo
 */
function track (eventName:PendoEvent, payload?: Record<PropertyKey, unknown>) {
  if ((store.state as unknown as ObjectWith<{ global: GlobalState }>).global.isPendoLoaded) {
    window.pendo.track(eventName, payload)
  }
}

/**
 * Determines the PDF content type based on the file properties for Pendo tracking purposes.
 * @param file {Filder} The file for which to determine the PDF content type
 * @returns {String|null} The PDF content type ('text', 'scanned', 'converted' or null if not a PDF)
 */
function getPdfContentType (file: Filder) {
  if (file.ext === 'pdf') {
    return file.pdfHasText ? 'text' : 'scanned'
  } else {
    if (file.ext && WORD_EXTENSIONS.includes(file.ext) && file.pdfHasText) {
      return 'converted'
    }
    return null
  }
}


/**
 * User role constants for Pendo tracking purposes.
 */
const USER_ROLE = {
  PROJECT_MANAGER: 'project_manager',
  GUEST: 'guest',
} as const

const IMPORT_SOURCES = {
  EXCEL: 'excel',
} as const

export { track, getPdfContentType, USER_ROLE, IMPORT_SOURCES }